import { PhoneIncoming, PhoneOutgoing, PhoneMissed, Phone, Trash2, User } from 'lucide-react'
import useHistoryStore from '../stores/historyStore'
import useSipStore from '../stores/sipStore'
import usePhonebookStore, { normalisePhone, formatPhone } from '../stores/phonebookStore'

function formatTime(iso) {
  const d = new Date(iso)
  const now = new Date()
  const diff = (now - d) / 1000
  if (diff < 60) return 'עכשיו'
  if (diff < 3600) return `לפני ${Math.floor(diff / 60)} ד׳`
  if (diff < 86400) return d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })
  return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' })
}

function formatDuration(sec) {
  if (!sec) return '—'
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return m ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`
}

export default function CallHistory() {
  const calls = useHistoryStore(s => s.calls)
  const clear = useHistoryStore(s => s.clear)
  const dial = useSipStore(s => s.dial)
  const status = useSipStore(s => s.status)
  const inCall = useSipStore(s => s.inCall)
  const phonebook = usePhonebookStore(s => s.contacts)

  // Memoise phone → contact map for fast labelling
  const phoneToContact = (() => {
    const m = new Map()
    for (const c of phonebook) for (const p of c.phones) m.set(p, c)
    return m
  })()

  if (calls.length === 0) {
    return (
      <div className="text-center text-mute py-12">
        <Phone size={40} className="mx-auto mb-2 opacity-40" />
        <div className="text-sm">אין שיחות עדיין</div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-text">היסטוריה</h2>
        <button
          onClick={() => confirm('למחוק את כל ההיסטוריה?') && clear()}
          className="text-mute hover:text-err p-1"
          title="נקה היסטוריה"
        >
          <Trash2 size={16} />
        </button>
      </div>
      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {calls.map(c => {
          const Icon = c.direction === 'in'
            ? (c.status === 'ended' || c.status === 'answered' ? PhoneIncoming : PhoneMissed)
            : PhoneOutgoing
          const iconColor = c.status === 'failed' || c.status === 'missed' ? 'text-err' :
                            c.direction === 'in' ? 'text-emerald-400' : 'text-brand'
          return (
            <div
              key={c.id}
              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-line/30 group"
            >
              <Icon size={16} className={iconColor + ' shrink-0'} />
              <div className="flex-1 min-w-0">
                {(() => {
                  const contact = phoneToContact.get(normalisePhone(c.number))
                  return contact ? (
                    <>
                      <div className="text-sm font-medium truncate flex items-center gap-1">
                        <User size={11} className="text-brand shrink-0" />
                        {contact.name}
                      </div>
                      <div className="font-mono ltr text-[11px] text-mute">{formatPhone(c.number)}</div>
                    </>
                  ) : (
                    <div className="font-mono ltr text-sm truncate">{formatPhone(c.number)}</div>
                  )
                })()}
                <div className="text-xs text-mute">
                  {formatTime(c.startedAt)} · {formatDuration(c.durationSec)}
                  {c.cause && <span className="ltr"> · {c.cause}</span>}
                </div>
              </div>
              <button
                onClick={() => { if (status === 'registered' && !inCall) dial(c.number) }}
                disabled={status !== 'registered' || inCall}
                className="opacity-0 group-hover:opacity-100 p-1.5 bg-brand/20 hover:bg-brand/40
                  rounded-lg text-brand disabled:bg-line/20 disabled:text-mute"
                title="חייג שוב"
              >
                <Phone size={14} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
